import React, { memo, useEffect, useState } from 'react';
import { mutationTypes, table_da_possessive_headers, table_en_possessive_headers, table_number_headers, table_possessive_headers } from './mutationUtils';
import { newAdjQual, newArticle, newNoun, wordGenders, wordNumbers, wordTopics } from './wordUtils';
import { applyMutations } from './mutations';
import MutationTable from './MutationTable';
import MutationSentences, { WORD_TEMPLATE } from './MutationSentences';
import {
  Box,
  Card,
  CardContent,
  Grid,
  TextField, ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import HelpForInput from './HelpForInput';
import LaunchOutlinedIcon from '@mui/icons-material/LaunchOutlined';
import InfoForArticle from './InfoForArticle';
import BtnOpenMutation from './BtnOpenMutation';
import Link from '@mui/material/Link';
import { getDicoUrl, sectionsForAdjective, sectionsForNoun } from './vocabularyUtils';

const NAME_PARAMETERS = {
  wordGender: 'wordGender',
  wordNumber: 'wordNumber',
  wordTopic: 'wordTopic',
};

const MutationsWithInputForNoun = () => {

  // TODO complete ar/ur explanation
  // TODO review list style in help
  // TODO add adjective input, sentences
  // TODO save user prefs in localStorage

  const [ nounText, setNounText ] = useState('');
  const [ nounToMutate, setNounToMutate ] = useState(newNoun(''));

  const [ adjectiveText, setAdjectiveText ] = useState('');
  const [ adjectiveToMutate, setAdjectiveToMutate ] = useState(newAdjQual(''));

  const sentencesForNoun = [
    { sentence: `Pelec'h emañ <b>ma/he/hon/o</b> ${WORD_TEMPLATE}?`, mutationType: mutationTypes.SPIRANTE },
    { sentence: `Pelec'h emañ <b>da/e</b> ${WORD_TEMPLATE}?`, mutationType: mutationTypes.SOFT },
    { sentence: `Pelec'h emañ <b>ho</b> ${WORD_TEMPLATE}?`, mutationType: mutationTypes.HARD },
    { sentence: `Debret 'm eus <b>an holl</b> ${WORD_TEMPLATE}.`, mutationType: mutationTypes.SOFT },
    { sentence: `Te <b>pe</b> ${WORD_TEMPLATE}?`, mutationType: mutationTypes.SOFT },
    { sentence: `Ober a rit kement <b>a</b> ${WORD_TEMPLATE} ha ni!`, mutationType: mutationTypes.SOFT },
    { sentence: `Anavezout a rit muioc'h <b>a</b> ${WORD_TEMPLATE} egedomp.`, mutationType: mutationTypes.SOFT },
    { sentence: `Gwelet hon eus nebeutoc'h <b>a</b> ${WORD_TEMPLATE} hiziv eget dec'h.`, mutationType: mutationTypes.SOFT },
    { sentence: `<b>Dre</b> ${WORD_TEMPLATE} emaon o vont.`, mutationType: mutationTypes.SOFT },
  ];


  const getSentenceForArticleNounAdjective = (noun, adjective) => {

    const ar = newArticle('ar');
    const copyNoun = noun.copy();
    const copyAdjective = adjective.copy();

    applyMutations([ ar, copyNoun, copyAdjective ]);

    const fullSentence = {
      sentence: '',
      copyNoun,
      copyAdjective,
    };

    const hasNounMutated = copyNoun.resultContent !== copyNoun.sourceContent;
    if (hasNounMutated) {
      fullSentence.sentence = `<em class="${copyNoun.mutationCause}">${ar.resultContent} ${copyNoun.resultContent}</em>`;
    } else {
      fullSentence.sentence = `${ar.resultContent} ${copyNoun.resultContent}`;
    }

    const hasAdjectiveMutated = copyAdjective.resultContent !== copyAdjective.sourceContent;
    if (hasAdjectiveMutated) {
      fullSentence.sentence = fullSentence.sentence + ` <em class="${copyAdjective.mutationCause}">${copyAdjective.resultContent}</em>`;
    } else {
      fullSentence.sentence = fullSentence.sentence + ` ${copyAdjective.resultContent}`;
    }

    return fullSentence;
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const copyNoun = nounToMutate.copy();
      copyNoun.sourceContent = nounText;
      console.log('=> setNounToMutate', nounText);
      setNounToMutate(copyNoun);
    }, 500);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ nounText ]);

  useEffect(() => {
    if (nounToMutate.sourceContent !== nounText) {
      setNounText(nounToMutate.sourceContent);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ nounToMutate ]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const copyAdjective = adjectiveToMutate.copy();
      copyAdjective.sourceContent = adjectiveText;
      console.log('=> setAdjectiveToMutate', adjectiveText);
      setAdjectiveToMutate(copyAdjective);
    }, 500);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ adjectiveText ]);

  useEffect(() => {
    if (adjectiveToMutate.sourceContent !== adjectiveText) {
      setAdjectiveText(adjectiveToMutate.sourceContent);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ adjectiveToMutate ]);

  const handleChangeForNoun = (event, nameParameter, newValue) => {
    const target = event.target;

    if (target.name === 'noun-text') {
      setNounText(target.value);

    } else if (nameParameter === NAME_PARAMETERS.wordGender && newValue !== null) {
      const copyNoun = nounToMutate.copy();
      copyNoun.wordGender = newValue;
      setNounToMutate(copyNoun);

    } else if (nameParameter === NAME_PARAMETERS.wordNumber && newValue !== null) {
      const copyNoun = nounToMutate.copy();
      copyNoun.wordNumber = newValue;
      setNounToMutate(copyNoun);

    } else if (nameParameter === NAME_PARAMETERS.wordTopic && newValue !== null) {
      const copyNoun = nounToMutate.copy();
      copyNoun.wordTopic = newValue;
      setNounToMutate(copyNoun);
    }

  };

  const handleChangeForAdjective = (event) => {
    const target = event.target;
    if (target.name === 'adjective-text') {
      setAdjectiveText(target.value);
    }
  };

  const sentenceAndCopy = getSentenceForArticleNounAdjective(nounToMutate, adjectiveToMutate);

  return (
    <section className={'mutation-dynamic-noun '}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <form name={'mutation-form-noun'} className={'mutation-form-noun'} noValidate>
                <div className={'input-with-info'}>
                  <TextField id={'inputNounToMutate'}
                             variant="standard"
                             label={'Noun'}
                             name="noun-text"
                             value={nounText}
                             onChange={(e) => handleChangeForNoun(e)}
                             fullWidth={true}
                  />

                  <HelpForInput setWordToMutate={setNounToMutate} sections={sectionsForNoun} title={'Test with a noun'} />
                </div>

                {nounToMutate.sourceContent && (
                  <Box>
                    <Link href={getDicoUrl(nounToMutate.sourceContent)}
                          target={'_blank'}
                    >
                      <small>Devri <LaunchOutlinedIcon fontSize={'0.8em'} /></small>
                    </Link>
                    <Link href={`https://glosbe.com/br/fr/${nounToMutate.sourceContent}`}
                          target={'_blank'}
                          sx={{
                            marginInlineStart: '1rem',
                          }}>
                      <small>Glosbe <LaunchOutlinedIcon fontSize={'0.8em'} /></small>
                    </Link>
                    <Link href={`https://br.wiktionary.org/wiki/${nounToMutate.sourceContent}`}
                          target={'_blank'}
                          sx={{
                            marginInlineStart: '1rem',
                          }}>
                      <small>Wiktionary <LaunchOutlinedIcon fontSize={'0.8em'} /></small>
                    </Link>
                  </Box>
                )}

                <ToggleButtonGroup
                  color="primary"
                  value={nounToMutate.wordGender}
                  exclusive
                  onChange={(event, newValue) => handleChangeForNoun(event, NAME_PARAMETERS.wordGender, newValue)}
                  sx={{
                    marginBlockStart: '1rem',
                  }}
                >
                  <ToggleButton value={wordGenders.MASCULINE}>Masculine</ToggleButton>
                  <ToggleButton value={wordGenders.FEMININE}>Feminine</ToggleButton>
                </ToggleButtonGroup>

                <ToggleButtonGroup
                  color="primary"
                  value={nounToMutate.wordNumber}
                  exclusive
                  onChange={(event, newValue) => handleChangeForNoun(event, NAME_PARAMETERS.wordNumber, newValue)}
                  sx={{
                    marginBlockStart: '1rem',
                  }}
                >
                  <ToggleButton value={wordNumbers.SINGULAR}>Singular</ToggleButton>
                  <ToggleButton value={wordNumbers.PLURAL}>Plural</ToggleButton>
                </ToggleButtonGroup>

                <ToggleButtonGroup
                  color="primary"
                  value={nounToMutate.wordTopic}
                  exclusive
                  onChange={(event, newValue) => handleChangeForNoun(event, NAME_PARAMETERS.wordTopic, newValue)}
                  sx={{
                    marginBlockStart: '1rem',
                  }}
                >
                  <ToggleButton value={wordTopics.OTHER}>Other</ToggleButton>
                  <ToggleButton value={wordTopics.PEOPLE}>People</ToggleButton>
                </ToggleButtonGroup>

              </form>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={false} md={6}>
          <Card>
            <CardContent>
              <form name={'mutation-form-adjective'} noValidate>
                <Box sx={{
                  display: 'flex',
                }}>
                  <TextField id={'inputAdjectiveToMutate'}
                             variant="standard"
                             label={'Adjective'}
                             name="adjective-text"
                             value={adjectiveText}
                             onChange={(e) => handleChangeForAdjective(e)}
                             fullWidth={true}
                  />

                  <HelpForInput setWordToMutate={setAdjectiveToMutate} sections={sectionsForAdjective} title={'Test with an adjective'} />
                </Box>
              </form>
            </CardContent>
          </Card>
        </Grid>

        {nounToMutate.sourceContent &&
        <>
          <Grid item xs={12}>
            <Card>
              <CardContent className={'card-article'}>
                <span className={'card-article__sentence'}
                      dangerouslySetInnerHTML={{ __html: sentenceAndCopy.sentence }}></span>
                <div className={'card-article__infos'}>
                  {sentenceAndCopy.copyNoun.mutationCause && <BtnOpenMutation mutationType={sentenceAndCopy.copyNoun.mutationCause} />}
                  {sentenceAndCopy.copyAdjective.mutationCause && <BtnOpenMutation mutationType={sentenceAndCopy.copyAdjective.mutationCause} />}
                  <InfoForArticle />
                </div>
              </CardContent>
            </Card>
          </Grid>
        </>
        }

        {nounToMutate.sourceContent &&
        <Grid item xs={12}>
          <MutationSentences sentences={sentencesForNoun} word={nounToMutate} />
        </Grid>
        }

        {nounToMutate.sourceContent &&
        <Grid item lg={6} md={12}>
          <MutationTable title={'Possessive'} word={nounToMutate} rowHeaders={table_possessive_headers} />
        </Grid>
        }

        {nounToMutate.sourceContent &&
        <Grid item lg={6} md={12}>
          <MutationTable title={'En+Possessive'} word={nounToMutate} rowHeaders={table_en_possessive_headers} />
        </Grid>
        }

        {nounToMutate.sourceContent &&
        <Grid item lg={6} md={12}>
          <MutationTable title={'Da+Possessive'} word={nounToMutate} rowHeaders={table_da_possessive_headers} />
        </Grid>
        }

        {nounToMutate.sourceContent &&
        <Grid item lg={6} md={12}>
          <MutationTable title={'Numerals'} word={nounToMutate} rowHeaders={table_number_headers} />
        </Grid>
        }

      </Grid>
    </section>
  );
};

export default memo(MutationsWithInputForNoun);
